import { Controller } from "@hotwired/stimulus"
import { debounce } from "../utils"

// Live "Invoice Preview" for the new custom invoice form. On any form change it
// debounces, serializes the form, and POSTs it to the preview endpoint, swapping
// the returned server-rendered preview into the output target. Disabled inputs
// (e.g. lease-only fields on non-lease rows) are excluded by FormData, matching
// what the create action would receive.
//
// Don't `refresh()` on `connect` because form server-renders with what it needs.
export default class extends Controller {
  static targets = ["form", "output"]
  static values = { url: String }

  schedule = debounce(() => this.refresh(), 300)

  // An output rendered by the server (e.g. a turbo frame load) is newer than any pending preview.
  outputTargetConnected() {
    this.previewRequest?.abort()
  }

  // A newer change cancels the pending preview so a slow, stale response can't overwrite a fresh one.
  refresh = async () => {
    this.previewRequest?.abort()
    const request = (this.previewRequest = new AbortController())
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    // The preview endpoint is always POST. Drop the form's `_method` override
    // (e.g. `patch` on the edit form) so Rack doesn't rewrite this to PATCH.
    const body = new FormData(this.formTarget)
    body.delete("_method")

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, Accept: "text/html" },
        body,
        signal: request.signal,
      })

      if (!response.ok) return

      const html = await response.text()
      this.outputTarget.innerHTML = html
    } catch {
      // Ignore preview failures and cancellations; the form remains usable.
    }
  }
}
